// =====================================================================
//  Analyse statique d'un sujet / corps Handlebars EASYMAIL
//  ---------------------------------------------------------------------
//  Ne remplace pas le vrai moteur (HandlebarsDotNet, appelé via
//  /process/preview) : détecte en amont les fautes que le moteur
//  signale mal (variable absente, helper inconnu, bloc mal fermé,
//  `{{` dans un commentaire HTML…).
//  Même logique que skills/easymail-templates/scripts/check_package.py :
//  toute évolution des helpers (ApiCore/MergeTemplate/HandlerbarsHelpers.cs)
//  doit être reportée dans les deux.
// =====================================================================

import {PackageReport} from "./PackageReport.js";

export const INLINE_HELPERS = new Set([
    "uppercase", "lowercase", "capitalize", "truncate", "default",
    "formatDate", "formatNumber", "pluralize", "log",
]);
export const BLOCK_HELPERS = new Set([
    "if", "unless", "each", "with",
    "ifHasValue", "ifEquals", "ifNotEquals", "findBy",
]);
const SCOPE_BLOCKS = new Set(["each", "with", "findBy"]);
const UNSAFE_IF_ABSENT = new Set(["uppercase", "lowercase", "capitalize"]);
const LITERALS = new Set(["true", "false", "null", "undefined"]);

const TOKEN_RE = /"(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'|\(|\)|[^\s()]+/g;
const TAG_RE = /\{\{(\{?)([\s\S]*?)\}?\}\}/g;
const NUMBER_RE = /^-?\d+(\.\d+)?$/;

const MAIL_UNSAFE: Array<[RegExp, string]> = [
    [/<script\b/i, "<script>"],
    [/<form\b/i, "<form>"],
    [/<iframe\b/i, "<iframe>"],
    [/<(video|audio|object|embed)\b/i, "média embarqué"],
    [/<link\b[^>]*stylesheet/i, "<link rel=stylesheet>"],
    [/@import\b/i, "@import"],
    [/\bon[a-z]+\s*=\s*["']/i, "attribut JavaScript (onclick…)"],
];
const MAIL_LAYOUT_UNSAFE: Array<[RegExp, string]> = [
    [/position\s*:\s*(absolute|fixed)/i, "position absolute/fixed"],
    [/display\s*:\s*(flex|grid|inline-flex)/i, "display flex/grid"],
];

/** Contexte non résoluble (collection absente, élément primitif…) : pas de contrôle à l'intérieur. */
const UNKNOWN = Symbol("unknown");
const MISSING = Symbol("missing");
type Ctx = unknown;

interface IBlock {
    name: string;
    line: number;
    scoped: boolean;
    protectedPaths: Set<string>;
}

export interface IAnalyseOptions {
    label: string;
    isBody: boolean;
    typeContent?: "html" | "text";
    typeProcess?: string;
}

export class HandlebarsAnalyzer {

    /** Analyse un texte ; renvoie l'ensemble des chemins de variables référencés. */
    static Analyse = (text: string, model: Record<string, unknown>, report: PackageReport, o: IAnalyseOptions): Set<string> => {
        const raw = text;
        text = HandlebarsAnalyzer.#blankComments(text);
        const refs = new Set<string>();
        const frames: Array<Ctx> = [model];
        const blocks: Array<IBlock> = [];
        const label = o.label;

        const protectedPaths = (): Set<string> => {
            const out = new Set<string>();
            blocks.forEach(b => b.protectedPaths.forEach(p => out.add(p)));
            return out;
        };

        const checkPath = (tok: string, line: number) => {
            const path = tok.includes("=") && !tok.startsWith("=") ? tok.split("=", 2)[1] : tok;
            if (!HandlebarsAnalyzer.#isPath(path))
                return;
            refs.add(path);
            if (HandlebarsAnalyzer.#resolve(path, frames, model) === MISSING) {
                const hint = frames.length > 1 ? " (variable du contexte parent ? essayer ../)" : "";
                report.Warning("W01", `${label} l.${line} : « ${path} » absent de modelTest${hint}`);
            }
        };

        let tagCount = 0;
        for (const m of text.matchAll(TAG_RE)) {
            tagCount++;
            const line = HandlebarsAnalyzer.#line(text, m.index ?? 0);
            const triple = m[1];
            const content = m[2].trim().replace(/^~|~$/g, "").trim();
            if (!content) {
                report.Error("E06", `${label} l.${line} : balise vide {{}}`);
                continue;
            }
            if (triple)
                report.Advice("A03", `${label} l.${line} : {{{${content}}}} insère du HTML brut non échappé`);

            const head = content[0];
            if ((head === "#" || head === "^") && content.length > 1 && content[1] !== " ") {
                const toks = content.slice(1).match(TOKEN_RE) ?? [];
                const name = toks[0] ?? "";
                const args = toks.slice(1);
                if (!BLOCK_HELPERS.has(name))
                    report.Error("E06", `${label} l.${line} : block helper inconnu « #${name} »`);
                const prot = new Set<string>();
                if ((name === "ifHasValue" || name === "if") && args.length && HandlebarsAnalyzer.#isPath(args[0]))
                    prot.add(args[0]);
                args.forEach(t => checkPath(t, line));

                let scoped = false;
                if (SCOPE_BLOCKS.has(name) && args.length && HandlebarsAnalyzer.#isPath(args[0])) {
                    frames.push(HandlebarsAnalyzer.#enterScope(name, args, frames, model, report, label, line));
                    scoped = true;
                }
                blocks.push({name, line, scoped, protectedPaths: prot});
            } else if (head === "/") {
                const name = content.slice(1).trim();
                const open = blocks.pop();
                if (!open) {
                    report.Error("E06", `${label} l.${line} : fermeture {{/${name}}} sans ouverture`);
                    continue;
                }
                if (open.name !== name)
                    report.Error("E06", `${label} l.${line} : {{/${name}}} ferme {{#${open.name}}} ouvert l.${open.line}`);
                if (open.scoped)
                    frames.pop();
            } else if (content === "else" || content === "^" || content.startsWith("else ")) {
                (content.match(TOKEN_RE) ?? []).slice(2).forEach(t => checkPath(t, line)); // {{else if X}}
                // La branche else d'un each/with/findBy s'exécute dans le contexte parent.
                if (blocks.length && blocks[blocks.length - 1].scoped)
                    frames[frames.length - 1] = frames.length > 1 ? frames[frames.length - 2] : model;
            } else if (head === ">") {
                report.Error("E06", `${label} l.${line} : partial « ${content} » — aucun partial n'est enregistré dans EASYMAIL`);
            } else {
                const toks = content.match(TOKEN_RE) ?? [];
                const name = toks[0];
                if (INLINE_HELPERS.has(name)) {
                    if (name === "log")
                        report.Advice("A01", `${label} l.${line} : {{log …}} à retirer avant mise en production`);
                    const args = toks.slice(1).filter(t => t !== "(" && t !== ")");
                    args.filter(t => !INLINE_HELPERS.has(t)).forEach(t => checkPath(t, line));
                    if (UNSAFE_IF_ABSENT.has(name) && args.length && HandlebarsAnalyzer.#isPath(args[0]) && !protectedPaths().has(args[0]))
                        report.Warning("W03", `${label} l.${line} : « ${name} ${args[0]} » sans protection — encadrer par {{#ifHasValue ${args[0]}}} si la variable peut manquer`);
                } else if (toks.length > 1) {
                    report.Error("E06", `${label} l.${line} : helper inconnu « ${name} »`);
                } else {
                    checkPath(name, line);
                }
            }
        }
        blocks.forEach(b => report.Error("E06", `${label} l.${b.line} : bloc {{#${b.name}}} jamais fermé`));
        if ((text.match(/\{\{/g) ?? []).length !== tagCount)
            report.Error("E06", `${label} : accolades {{ non refermées`);

        if (o.isBody)
            HandlebarsAnalyzer.#checkBody(raw, text, report, o);
        return refs;
    };

    static #checkBody = (raw: string, text: string, report: PackageReport, o: IAnalyseOptions) => {
        for (const c of text.matchAll(/<!--([\s\S]*?)-->/g)) {
            if (c[1].includes("{{"))
                report.Warning("W02", `${o.label} l.${HandlebarsAnalyzer.#line(text, c.index ?? 0)} : {{ dans un commentaire HTML <!-- --> — utiliser {{!-- --}}`);
        }
        if (o.typeContent === "html") {
            const rules = o.typeProcess === "pdf" ? MAIL_UNSAFE : MAIL_UNSAFE.concat(MAIL_LAYOUT_UNSAFE);
            rules.forEach(([rx, what]) => {
                const hit = rx.exec(raw);
                if (hit)
                    report.Warning("W04", `${o.label} l.${HandlebarsAnalyzer.#line(raw, hit.index)} : ${what} — peu compatible avec les clients mail`);
            });
            if (!/<[a-zA-Z][^>]*>/.test(raw))
                report.Warning("W06", `${o.label} : typeContent = html mais aucune balise HTML`);
        } else if (/<(p|br|div|table|span|a|b|strong|html)\b[^>]*>/i.test(raw)) {
            report.Warning("W06", `${o.label} : typeContent = text mais le corps contient des balises HTML`);
        }
    };

    static #enterScope = (name: string, args: Array<string>, frames: Array<Ctx>, model: Record<string, unknown>,
                          report: PackageReport, label: string, line: number): Ctx => {
        const val = HandlebarsAnalyzer.#resolve(args[0], frames, model);
        if (name === "each") {
            if (!Array.isArray(val))
                return UNKNOWN;
            if (val.length < 2)
                report.Advice("A04", `${label} l.${line} : « ${args[0]} » contient moins de 2 éléments dans modelTest — la répétition ne sera pas visible en prévisualisation`);
            return HandlebarsAnalyzer.#isObject(val[0]) ? val[0] : UNKNOWN;
        }
        if (name === "with")
            return HandlebarsAnalyzer.#isObject(val) ? val : UNKNOWN;
        // findBy Collection "Clé" "Valeur"
        if (!Array.isArray(val))
            return UNKNOWN;
        const key = args[1]?.replace(/^["']|["']$/g, "");
        const want = (args[2]?.replace(/^["']|["']$/g, "") ?? "").toLowerCase();
        const items = val.filter(HandlebarsAnalyzer.#isObject) as Array<Record<string, unknown>>;
        const match = items.find(x => key && String(x[key] ?? "").toLowerCase() === want);
        return match ?? items[0] ?? UNKNOWN;
    };

    /** Résout un chemin Handlebars ; renvoie la valeur, UNKNOWN ou MISSING. */
    static #resolve = (path: string, frames: Array<Ctx>, root: Record<string, unknown>): Ctx => {
        let ctx: Ctx;
        if (path.startsWith("@root")) {
            ctx = root;
            path = path.slice(5).replace(/^\./, "");
            if (!path)
                return ctx;
        } else if (path.startsWith("@")) {
            return UNKNOWN; // @index, @first, @last, @key
        } else {
            let depth = 0;
            while (path.startsWith("../")) {
                depth++;
                path = path.slice(3);
            }
            const idx = frames.length - 1 - depth;
            if (idx < 0)
                return MISSING;
            ctx = frames[idx];
            if (path === "this" || path === ".")
                return ctx;
            if (path.startsWith("this."))
                path = path.slice(5);
            else if (path.startsWith("./"))
                path = path.slice(2);
        }
        for (let seg of path.split(/[./]/)) {
            if (ctx === UNKNOWN)
                return UNKNOWN;
            seg = seg.replace(/^\[|\]$/g, "");
            if (HandlebarsAnalyzer.#isObject(ctx)) {
                if (!(seg in ctx))
                    return MISSING;
                ctx = ctx[seg];
            } else if (Array.isArray(ctx) && /^\d+$/.test(seg)) {
                const i = Number(seg);
                if (i >= ctx.length)
                    return MISSING;
                ctx = ctx[i];
            } else {
                return MISSING;
            }
        }
        return ctx;
    };

    static #isObject = (v: unknown): v is Record<string, unknown> =>
        v !== null && typeof v === "object" && !Array.isArray(v);

    static #isPath = (tok: string): boolean =>
        !!tok && tok !== "(" && tok !== ")" && !LITERALS.has(tok) && !`"'`.includes(tok[0]) && !NUMBER_RE.test(tok);

    /** Remplace les commentaires Handlebars par des espaces (numéros de ligne conservés). */
    static #blankComments = (text: string): string => {
        const blank = (m: string) => m.replace(/[^\n]/g, " ");
        return text
            .replace(/\{\{~?!--[\s\S]*?--~?\}\}/g, blank)
            .replace(/\{\{~?![^}]*\}\}/g, blank);
    };

    static #line = (text: string, pos: number): number =>
        (text.slice(0, pos).match(/\n/g) ?? []).length + 1;
}
